/**
 * Guided Flow System - Public API
 *
 * A comprehensive framework for orchestrating multi-step guided experiences
 * including demos, tutorials, and onboarding flows.
 */

// ============================================================================
// Core Types
// ============================================================================

export type {
  // Core types
  FlowStep,
  FlowStepType,
  FlowContext,
  GuidedFlowDefinition,
  FlowConfig,
  FlowControllerAPI,
  FlowEvent,
  FlowEventType,
  FlowEventHandler,

  // Configuration types
  FlowViewConfig,
  ChatConfig,
  InteractionConfig,
  TransitionConfig,
  DecisionConfig,

  // Sub-types
  FlowViewType,
  ViewAnnotation,
  InteractionHint,
  ChatMessage as FlowChatMessage,
  MessageAction as FlowMessageAction,
  InteractionAction,
  MockFile,
  DecisionOption,
} from './core/types';

// ============================================================================
// Controller
// ============================================================================

export {
  GuidedFlowController,
  flowRegistry,
} from './core/GuidedFlowController';

import { flowRegistry } from './core/GuidedFlowController';

// ============================================================================
// Providers
// ============================================================================

export type {
  NavigationProvider,
  ModalProvider,
  ChatProvider,
  NotificationProvider,
  UIProvider,
  DataProvider,
  AnalyticsProvider,
  FlowProviders,
  ChatMessage,
  MessageAction,
  FileUploadResult,
  NotificationType,
} from './providers';

export {
  FlowProvider,
  useFlowProviders,
  useProvider,
  createStubProviders,
  // Individual stub providers (for custom overrides)
  StubNavigationProvider,
  StubModalProvider,
  StubChatProvider,
  StubNotificationProvider,
  StubUIProvider,
  StubDataProvider,
  StubAnalyticsProvider,
} from './providers';

// ============================================================================
// React Components
// ============================================================================

export { GuidedFlow } from './components/GuidedFlow';
export { FlowRenderer } from './components/FlowRenderer';
export { ChatStepRenderer } from './components/ChatStepRenderer';
export { ViewStepRenderer } from './components/ViewStepRenderer';
export { InteractionStepRenderer } from './components/InteractionStepRenderer';
export { TransitionRenderer } from './components/TransitionRenderer';
export { DecisionRenderer } from './components/DecisionRenderer';

// ============================================================================
// View Registry
// ============================================================================

export {
  VIEW_REGISTRY,
  getViewComponent,
  registerViewComponent,
} from './components/ViewRegistry';

// ============================================================================
// Pre-built Flows
// ============================================================================

export { mainDemoFlow } from './flows/mainDemoFlow';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Register a flow definition for use in the application
 *
 * Usage:
 * ```tsx
 * import { registerFlow, mainDemoFlow } from '@/features/guided-flow';
 *
 * registerFlow(mainDemoFlow);
 * ```
 */
export { flowRegistry as registry } from './core/GuidedFlowController';

export function registerFlow(flow: any): void {
  flowRegistry.registerFlow(flow);
}

/**
 * Get all registered flows
 */
export function getAvailableFlows(): any[] {
  return flowRegistry.getAllFlows();
}
